import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useAuth } from 'context/AuthContext';
import { getGroup, getTasksManagerQtd } from 'services/dashboard.service';
import { isGrupoValido } from 'utils/helpers';
import { readOncePerSession, SESSION_KEYS } from 'utils/session-reads';

type MenuData = {
  showCatalog: boolean;
  /** Realizações roteadas ao gestor (`tasks_manager_qtd`); a Home lê daqui em vez de buscar de novo. */
  tasksManager?: any;
  tasksDuplaManager?: any;
};

const MenuDataContext = createContext<MenuData>({ showCatalog: false });

// Os efeitos dependem só de primitivos (DCE-28): uma nova referência de `user`/`userInfo` com os
// mesmos valores, como a que o refreshUser publica, não refaz nenhuma leitura.
export function MenuDataProvider({ children }: { children: ReactNode }) {
  const { user, userInfo, realCycle, cycleSelected } = useAuth();

  const [showCatalog, setShowCatalog] = useState(false);
  const [tasksManager, setTasksManager] = useState<any>();
  const [tasksDuplaManager, setTasksDuplaManager] = useState<any>();

  const userId = user?._id;
  const extraId = user?.extra?.id;
  const hasUserInfo = !!userInfo;
  const isManager = !!userInfo?.is_manager;
  const grupoNome = userInfo?.grupo?.nome;
  const madDupla = userInfo?.mad_dupla;
  const duplaId = Array.isArray(madDupla) && madDupla.length ? madDupla[0]._id : undefined;
  const cycleId = cycleSelected?._id;
  const cycleTitle = cycleSelected?.title;
  const hasRealCycle = !!realCycle?.length;
  const cycleDates = realCycle?.find((item: any) => item._id === cycleId);
  const cycleStart = cycleDates?.date_start;
  const cycleEnd = cycleDates?.date_end;

  // `get_group` é configuração do ciclo: 1 vez por sessão por player e ciclo (DCE-12, DCE-30).
  useEffect(() => {
    if (!userId || !hasUserInfo) return;

    let cancelado = false;

    readOncePerSession(SESSION_KEYS.group(userId, cycleTitle), () => getGroup(cycleTitle))
      .then(group => {
        const catalog = group || { grupo: grupoNome };
        if (!cancelado) setShowCatalog(!!catalog?.grupo && isGrupoValido(catalog.grupo));
      })
      .catch(error => console.error('Erro ao carregar o grupo do menu:', error));

    return () => {
      cancelado = true;
    };
  }, [userId, hasUserInfo, cycleTitle, grupoNome]);

  // `tasks_manager_qtd` só existe para gestor (DCE-18).
  useEffect(() => {
    if (!userId || !isManager || !hasRealCycle) return;

    let cancelado = false;

    async function carregarTarefas() {
      const [data, dataDupla] = await Promise.all([
        getTasksManagerQtd(extraId, cycleStart, cycleEnd),
        duplaId ? getTasksManagerQtd(duplaId, cycleStart, cycleEnd) : undefined,
      ]);

      if (cancelado) return;

      setTasksManager(data);
      if (duplaId) setTasksDuplaManager(dataDupla);
    }

    carregarTarefas();

    return () => {
      cancelado = true;
    };
  }, [userId, isManager, hasRealCycle, extraId, duplaId, cycleStart, cycleEnd]);

  const value = useMemo(
    () => ({ showCatalog, tasksManager, tasksDuplaManager }),
    [showCatalog, tasksManager, tasksDuplaManager],
  );

  return <MenuDataContext.Provider value={value}>{children}</MenuDataContext.Provider>;
}

export const useMenuData = () => useContext(MenuDataContext);
